import {
  isGeopoliticalHazardType,
  normalizeHazardType,
  type HazardLike,
} from '../hazardTypes';

/**
 * Provider-neutral hazard presentation.
 *
 * Google (`HomeMapPolygon`) and Mapbox (`HomeMapboxMap`) draw hazards with
 * different SDK mechanics, but the decisions about *what* to show -- title,
 * category, severity wording and colour, which icon, which polygon colours --
 * are the same product rules. They live here once, as pure data, so neither
 * provider owns a private copy of the tables.
 *
 * Icons are returned as semantic keys rather than bundled asset URLs so this
 * module stays free of image imports and directly testable; `hazardIcons.ts`
 * resolves a key to the shared asset both providers already use.
 */

export type HazardCategory = 'weather' | 'geopolitical';

export type HazardIconKey =
  | 'wildfire'
  | 'flood'
  | 'tornado'
  | 'extremeHeat'
  | 'dustStorm'
  | 'highWind'
  | 'snowstorm'
  | 'freeze'
  | 'severeThunderstorm'
  | 'heavyRain'
  | 'heavyFog'
  | 'nonWeather'
  | 'cloud';

export type HazardPolygonPresentation = {
  fillColor: string;
  strokeColor: string;
  fillOpacity: number;
  strokeWeight: number;
};

export type HazardPresentation = {
  /** NewsFlash headline, siren decoration removed, hazard type as fallback. */
  displayTitle: string;
  /** Canonical hazard type, used where the type itself is the heading. */
  typeLabel: string;
  category: HazardCategory;
  /** Customer-facing wording for the category, e.g. in accessible labels. */
  categoryLabel: string;
  severityLabel: string;
  severityColor: string;
  effective: string;
  expiration: string;
  updated: string;
  tooltipIconKey: HazardIconKey;
  markerIconKey: HazardIconKey;
  polygon: HazardPolygonPresentation;
};

export type HazardAlertLike = HazardLike & {
  title?: string | null;
  title_without_date?: string | null;
  alert_level?: string | null;
  effective_date?: string | null;
  expiration_date?: string | null;
  updated_at?: string | null;
};

const inheritWhenBlank = (
  value: string | null | undefined,
  parentValue: string | null | undefined
): string | null | undefined =>
  value == null || value.trim() === '' ? parentValue : value;

/**
 * NewsFlash stores presentation fields at either the item or polygon level.
 * Normalize that shape before either provider asks for presentation data.
 */
export const inheritHazardPresentation = <T extends HazardAlertLike>(
  item: HazardAlertLike | null | undefined,
  polygon: T
): T =>
  ({
    ...polygon,
    hazard_type: inheritWhenBlank(polygon.hazard_type, item?.hazard_type),
    title: inheritWhenBlank(polygon.title, item?.title),
    title_without_date: inheritWhenBlank(
      polygon.title_without_date,
      item?.title_without_date
    ),
    alert_level: inheritWhenBlank(polygon.alert_level, item?.alert_level),
    effective_date: inheritWhenBlank(
      polygon.effective_date,
      item?.effective_date
    ),
    expiration_date: inheritWhenBlank(
      polygon.expiration_date,
      item?.expiration_date
    ),
    updated_at: inheritWhenBlank(polygon.updated_at, item?.updated_at),
  }) as T;

// All non-weather hazard types share one icon and one colour -- they are
// grouped together for customers as a single "non-weather" alert category.
const NON_WEATHER_ICON: HazardIconKey = 'nonWeather';
const NON_WEATHER_FILL = '#8B6F58';
const NON_WEATHER_STROKE = '#5F4B3B';

const ICON_KEY_BY_TYPE: Record<string, HazardIconKey> = {
  Wildfire: 'wildfire',
  'Flood Warning': 'flood',
  Tornado: 'tornado',
  'Extreme Heat': 'extremeHeat',
  'Dust Storm': 'dustStorm',
  'High Wind': 'highWind',
  Snowstorm: 'snowstorm',
  Freeze: 'freeze',
  'Severe Thunderstorm': 'severeThunderstorm',
  'Heavy Rain': 'heavyRain',
  'Heavy Fog': 'heavyFog',
  'Civil Unrest': NON_WEATHER_ICON,
  'Armed Conflict': NON_WEATHER_ICON,
  'Political and Trade Disruption': NON_WEATHER_ICON,
  'Operational Disruption': NON_WEATHER_ICON,
};

const FALLBACK_ICON_KEY: HazardIconKey = 'severeThunderstorm';

const POLYGON_COLORS_BY_TYPE: Record<
  string,
  { fillColor: string; strokeColor: string }
> = {
  Wildfire: { fillColor: '#FF4500', strokeColor: '#B22222' },
  'Flood Warning': { fillColor: '#1E90FF', strokeColor: '#104E8B' },
  Tornado: { fillColor: '#708090', strokeColor: '#2F4F4F' },
  'Extreme Heat': { fillColor: '#feb705', strokeColor: '#ff9500' },
  'Dust Storm': { fillColor: '#7CFC00', strokeColor: '#32CD32' },
  'High Wind': { fillColor: '#4682B4', strokeColor: '#27408B' },
  Snowstorm: { fillColor: '#00CED1', strokeColor: '#008B8B' },
  Freeze: { fillColor: '#8B0000', strokeColor: '#5C0000' },
  'Severe Thunderstorm': { fillColor: '#A0522D', strokeColor: '#5C3317' },
  'Heavy Rain': { fillColor: '#556B2F', strokeColor: '#3A5F0B' },
  'Heavy Fog': { fillColor: '#708090', strokeColor: '#2F4F4F' },
  'Civil Unrest': {
    fillColor: NON_WEATHER_FILL,
    strokeColor: NON_WEATHER_STROKE,
  },
  'Armed Conflict': {
    fillColor: NON_WEATHER_FILL,
    strokeColor: NON_WEATHER_STROKE,
  },
  'Political and Trade Disruption': {
    fillColor: NON_WEATHER_FILL,
    strokeColor: NON_WEATHER_STROKE,
  },
  'Operational Disruption': {
    fillColor: NON_WEATHER_FILL,
    strokeColor: NON_WEATHER_STROKE,
  },
};

const FALLBACK_POLYGON_COLORS = { fillColor: '#888', strokeColor: '#666' };

const POLYGON_SHAPE = {
  fillOpacity: 0.35,
  strokeWeight: 2,
};

const SEVERITY_BY_ALERT_LEVEL: Record<
  string,
  { label: string; color: string }
> = {
  L1: { label: 'Very low risk (L1)', color: '#00A550' },
  L2: { label: 'Low risk (L2)', color: '#F6BC2F' },
  L3: { label: 'Medium risk (L3)', color: '#FB8E2E' },
  L4: { label: 'High risk (L4)', color: '#F14664' },
  L5: { label: 'Very high risk (L5)', color: '#ED1C24' },
};

const UNKNOWN_SEVERITY = { label: 'Unknown risk', color: '#808080' };

const FALLBACK_TITLE = 'Hazard alert';

// Tooltips render their own hazard icon, so the NewsFlash siren decoration is
// dropped from the display title only. The stored title is left untouched.
const LEADING_SIREN = /^\s*🚨️?\s*/u;

const asText = (value?: string | null): string =>
  typeof value === 'string' ? value : '';

export const getHazardDisplayTitle = (
  alert?: HazardAlertLike | null
): string => {
  const title = alert?.title_without_date || alert?.title;
  if (typeof title === 'string') {
    const displayTitle = title.replace(LEADING_SIREN, '').trim();
    if (displayTitle) return displayTitle;
  }
  return normalizeHazardType(alert?.hazard_type) || FALLBACK_TITLE;
};

export const getHazardPresentation = (
  alert?: HazardAlertLike | null
): HazardPresentation => {
  const typeLabel = normalizeHazardType(alert?.hazard_type);
  const isGeopolitical = isGeopoliticalHazardType(alert?.hazard_type);
  const severity =
    SEVERITY_BY_ALERT_LEVEL[asText(alert?.alert_level)] ?? UNKNOWN_SEVERITY;
  const tooltipIconKey = ICON_KEY_BY_TYPE[typeLabel] ?? FALLBACK_ICON_KEY;

  return {
    displayTitle: getHazardDisplayTitle(alert),
    typeLabel,
    category: isGeopolitical ? 'geopolitical' : 'weather',
    categoryLabel: isGeopolitical ? 'Non-weather' : 'Weather',
    severityLabel: severity.label,
    severityColor: severity.color,
    effective: asText(alert?.effective_date),
    expiration: asText(alert?.expiration_date),
    updated: asText(alert?.updated_at),
    tooltipIconKey,
    // Weather hazards all share the generic cloud marker; non-weather hazards
    // carry their own icon out onto the map.
    markerIconKey: isGeopolitical ? tooltipIconKey : 'cloud',
    polygon: {
      ...POLYGON_SHAPE,
      ...(POLYGON_COLORS_BY_TYPE[typeLabel] ?? FALLBACK_POLYGON_COLORS),
    },
  };
};
